import { Button, Space } from 'antd';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type LocalCustomerManagementModuleSpecsFragment } from '../../../../api/fragments';
import SortableTransferField from '../../../../components/fields/SortableTransferField';
import CollapsibleWrapper, { Panel } from '../../../../components/wrappers/CollapsibleWrapper';
import { useKycFieldDefinitionsFromCountry } from '../../UpdateKYCPresetPage/KYCFieldDefinitions';
import useRenderKYCFieldName from '../../UpdateKYCPresetPage/useRenderKYCFieldName';

type KycFieldsFormProps = {
    company: LocalCustomerManagementModuleSpecsFragment['company'];
};

type RecordType = {
    key: string;
    title: string;
    description: string;
};

const filterOption = (inputValue: string, option: RecordType) =>
    option.title.toLowerCase().indexOf(inputValue.toLowerCase()) > -1;

const KYCFieldsForm = ({ company }: KycFieldsFormProps) => {
    const { t } = useTranslation(['localCustomerModuleDetails']);

    const renderKYCFieldName = useRenderKYCFieldName();

    const definitions = useKycFieldDefinitionsFromCountry(company.countryCode);

    const [dataSource, setDataSource] = useState(
        definitions.map(field => ({
            key: field.key,
            title: renderKYCFieldName(field.key),
            description: field.key,
        })) || []
    );
    const sortSelectedKeys = useCallback(
        (input: string[]) => definitions.filter(field => input.includes(field.key)).map(field => field.key),
        [definitions]
    );

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <CollapsibleWrapper defaultActiveKey={t('localCustomerModuleDetails:kycFieldsForm.title')}>
                <Panel
                    key={t('localCustomerModuleDetails:kycFieldsForm.title')}
                    extra={
                        <Button
                            form="updateKYCFields"
                            htmlType="submit"
                            onClick={e => e.stopPropagation()}
                            type="primary"
                        >
                            {t('localCustomerModuleDetails:kycFieldsForm.mainActionButtons.update')}
                        </Button>
                    }
                    header={t('localCustomerModuleDetails:kycFieldsForm.title')}
                >
                    <SortableTransferField
                        dataSource={dataSource}
                        filterOption={filterOption}
                        listStyle={{ width: '100%', height: 425 }}
                        name="kycFields"
                        preprocessTargetKeys={sortSelectedKeys}
                        render={item => item.title}
                        setDataSource={setDataSource}
                        titles={[
                            t('localCustomerModuleDetails:kycFieldsForm.subTitles.kycFieldPool'),
                            t('localCustomerModuleDetails:kycFieldsForm.subTitles.selectedKycField'),
                        ]}
                        showSearch
                    />
                </Panel>
            </CollapsibleWrapper>
        </Space>
    );
};

export default KYCFieldsForm;
